export interface Student {
  id: string;
  name: string;
  status: string;
  joinCode?: string;
  startPin?: string;
  checkedInRoom?: boolean;
  dapTestStatus?: { name: string };
  asmtSubtypeCd?: string;
  examStartTime?: string;
  estimatedExamEndTime?: string;
}

export interface Room {
  id: string;
  capacity: number;
  title: string;
  joinCode: string;
  startPin: string;
  students: Student[];
}

export interface FlowData {
  stepNum: number;
}

export interface CmsStep {
  step_number: number;
  main_panel_content?: string;
  main_panel_header?: string;
  detail_panel_content?: string;
  detail_panel_header?: string;
}

export interface CmsContent {
  steps: CmsStep[];
}

export enum STEP_COMPONENT {
  ABOUT_AP_EXAM = 'aboutAPExam',
  ABOUT_BREAKS = 'aboutBreaks',
  ACCOMMODATIONS_QUESTION = 'accommodationsQuestion',
  CHECK_DESKS_DIAGRAM = 'checkDesksDiagram',
  CHECK_IN_SCRIPT = 'checkInScript',
  CHECK_MATERIALS = 'checkMaterials',
  CHECK_SPACING = 'checkSpacing',
  CHECK_SUPPLIES = 'checkSupplies',
  CHECK_WIFI = 'checkWifi',
  COLLECT_PROHIBITED_ITEMS = 'collectProhibitedItems',
  COUNT_SEATS = 'countSeats',
  DEFAULT_CONTENT_TAB_DARK = 'defaultContentTabDark',
  DEFAULT_CONTENT_TAB_LIGHT = 'defaultContentTabLight',
  DEFAULT_SIDE_BY_SIDE_CONTENT = 'defaultSideBySideContent',
  DEVICES_AND_MATERIALS = 'devicesAndMaterials',
  DISTRIBUTE_SIGN_IN_TICKETS = 'distributeSignInTickets',
  DISTRIBUTE_SUPPLIES = 'distributeSupplies',
  READ_THE_SCRIPT = 'readTheScript',
  START_TESTING = 'startTesting',
  START_THE_TEST = 'startTheTest',
  TAKE_ATTENDANCE = 'takeAttendance',
  WELCOME = 'welcome',
  WRITE_INSTRUCTIONS = 'writeInstructions',
}

export interface FlowBounds {
  introStep: number;
  outerStep: number;
  progressStepperMax: number;
}

export interface FlowConfig<T> extends FlowBounds {
  steps: Record<number, T>;
}

export type ConfigKey = 'ap' | 'apHybrid' | 'apMT' | 'default';

import { isInSchoolAPEvent, isInSchoolAPHybridEvent, isInSchoolAPMTEvent } from '../../../utils/event';

/** Returns the config key used for EVENT_FLOW_CONFIGS lookup. */
// eslint-disable-next-line
export function getConfigKey(event: any): ConfigKey {
  if (isInSchoolAPMTEvent(event)) return 'apMT';
  if (isInSchoolAPEvent(event)) return 'ap';
  if (isInSchoolAPHybridEvent(event)) return 'apHybrid';
  return 'default';
}

export interface CompletionStepDetail {
  heading: string;
  imgClass: string;
  imgDesc: string;
}

export interface EventFlowConfig {
  setup: FlowConfig<STEP_COMPONENT>;
  attendance: FlowConfig<STEP_COMPONENT>;
  completion: FlowConfig<CompletionStepDetail>;
  /** The attendance step_number that the Monitor "Back" button links to. */
  lastAttendanceStep: number;
}

/**
 * Centralized step config for all wizard flows, keyed by event type.
 * Each flow maps step_number → renderer name (setup/attendance) or detail object (completion).
 */
export const EVENT_FLOW_CONFIGS: Record<ConfigKey, EventFlowConfig> = {
  // in-school sat/weekend
  default: {
    setup: {
      introStep: 0,
      outerStep: 7,
      progressStepperMax: 6,
      steps: {
        1: STEP_COMPONENT.COUNT_SEATS,
        2: STEP_COMPONENT.CHECK_SPACING,
        3: STEP_COMPONENT.CHECK_SUPPLIES,
        4: STEP_COMPONENT.CHECK_MATERIALS,
        5: STEP_COMPONENT.DISTRIBUTE_SIGN_IN_TICKETS,
        6: STEP_COMPONENT.WRITE_INSTRUCTIONS,
      },
    },
    attendance: {
      introStep: 1,
      outerStep: 10,
      progressStepperMax: 9,
      steps: {
        1: STEP_COMPONENT.TAKE_ATTENDANCE,
        2: STEP_COMPONENT.READ_THE_SCRIPT,
        3: STEP_COMPONENT.WELCOME,
        4: STEP_COMPONENT.DEVICES_AND_MATERIALS,
        5: STEP_COMPONENT.CHECK_DESKS_DIAGRAM,
        6: STEP_COMPONENT.ACCOMMODATIONS_QUESTION,
        7: STEP_COMPONENT.ABOUT_BREAKS,
        8: STEP_COMPONENT.START_TESTING,
        9: STEP_COMPONENT.START_THE_TEST,
      },
    },
    completion: {
      introStep: 1,
      outerStep: 3,
      progressStepperMax: 2,
      steps: {
        1: {
          heading: 'Verify Answer Submission',
          imgClass: 'test-complete-step-1-svg',
          imgDesc: 'Complete steps 1-3 before you dismiss students.',
        },
        2: {
          heading: 'Report Problems',
          imgClass: 'test-complete-step-2-svg',
          imgDesc: 'Report problems to your coordinator',
        },
      },
    },
    lastAttendanceStep: 10,
  },
  ap: {
    setup: {
      introStep: 0,
      outerStep: 7,
      progressStepperMax: 6,
      steps: {
        1: STEP_COMPONENT.ABOUT_AP_EXAM,
        2: STEP_COMPONENT.COUNT_SEATS,
        3: STEP_COMPONENT.CHECK_SUPPLIES,
        4: STEP_COMPONENT.CHECK_WIFI,
        5: STEP_COMPONENT.WRITE_INSTRUCTIONS,
        6: STEP_COMPONENT.COLLECT_PROHIBITED_ITEMS,
      },
    },
    attendance: {
      introStep: 0,
      outerStep: 10,
      progressStepperMax: 9,
      steps: {
        0: STEP_COMPONENT.CHECK_IN_SCRIPT,
        1: STEP_COMPONENT.TAKE_ATTENDANCE,
        2: STEP_COMPONENT.READ_THE_SCRIPT,
        3: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        4: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        5: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        6: STEP_COMPONENT.DEFAULT_CONTENT_TAB_LIGHT,
        7: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        8: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        9: STEP_COMPONENT.START_THE_TEST,
      },
    },
    completion: {
      introStep: 1,
      outerStep: 4,
      progressStepperMax: 3,
      steps: {
        1: {
          heading: 'Verify Answer Submission',
          imgClass: 'attendance-step-3-1',
          imgDesc: 'Complete steps 1-3 before you dismiss students.',
        },
        2: { heading: 'Report Problems', imgClass: 'setup-step-4-svg', imgDesc: 'Report problems to your coordinator' },
        3: {
          heading: 'All Finished',
          imgClass: 'wknd-sat-setup-complete-svg',
          imgDesc: 'Collect materials and dismiss students',
        },
      },
    },
    lastAttendanceStep: 9,
  },
  apHybrid: {
    setup: {
      introStep: 0,
      outerStep: 8,
      progressStepperMax: 7,
      steps: {
        1: STEP_COMPONENT.ABOUT_AP_EXAM,
        2: STEP_COMPONENT.COUNT_SEATS,
        3: STEP_COMPONENT.CHECK_SUPPLIES,
        4: STEP_COMPONENT.DISTRIBUTE_SUPPLIES,
        5: STEP_COMPONENT.CHECK_WIFI,
        6: STEP_COMPONENT.WRITE_INSTRUCTIONS,
        7: STEP_COMPONENT.COLLECT_PROHIBITED_ITEMS,
      },
    },
    attendance: {
      introStep: 0,
      outerStep: 13,
      progressStepperMax: 12,
      steps: {
        0: STEP_COMPONENT.CHECK_IN_SCRIPT,
        1: STEP_COMPONENT.TAKE_ATTENDANCE,
        2: STEP_COMPONENT.READ_THE_SCRIPT,
        3: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        4: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        5: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        6: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        7: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        8: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        9: STEP_COMPONENT.DEFAULT_CONTENT_TAB_LIGHT,
        10: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        11: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        12: STEP_COMPONENT.START_THE_TEST,
      },
    },
    completion: {
      introStep: 1,
      outerStep: 5,
      progressStepperMax: 4,
      steps: {
        1: {
          heading: 'Verify Answer Submission',
          imgClass: 'attendance-step-3-1',
          imgDesc: 'Complete steps 1-3 before you dismiss students.',
        },
        2: {
          heading: 'Collect Materials and Dismiss',
          imgClass: 'setup-step-4-svg',
          imgDesc: 'Collect materials and dismiss students',
        },
        3: {
          heading: 'Collect materials',
          imgClass: 'wknd-sat-setup-complete-svg',
          imgDesc: 'Collect materials and dismiss students',
        },
        4: {
          heading: 'Report Problems',
          imgClass: 'wknd-sat-setup-complete-svg',
          imgDesc: 'Report problems to your coordinator',
        },
      },
    },
    lastAttendanceStep: 12,
  },
  apMT: {
    setup: {
      introStep: 0,
      outerStep: 8,
      progressStepperMax: 7,
      steps: {
        1: STEP_COMPONENT.ABOUT_AP_EXAM,
        2: STEP_COMPONENT.COUNT_SEATS,
        3: STEP_COMPONENT.CHECK_SUPPLIES,
        4: STEP_COMPONENT.DISTRIBUTE_SUPPLIES,
        5: STEP_COMPONENT.CHECK_WIFI,
        6: STEP_COMPONENT.COLLECT_PROHIBITED_ITEMS,
        7: STEP_COMPONENT.WRITE_INSTRUCTIONS,
      },
    },
    attendance: {
      introStep: 0,
      outerStep: 15,
      progressStepperMax: 14,
      steps: {
        0: STEP_COMPONENT.CHECK_IN_SCRIPT,
        1: STEP_COMPONENT.TAKE_ATTENDANCE,
        2: STEP_COMPONENT.READ_THE_SCRIPT,
        3: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        4: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        5: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        6: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        7: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        8: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        9: STEP_COMPONENT.DEFAULT_CONTENT_TAB_LIGHT,
        10: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        11: STEP_COMPONENT.DEFAULT_CONTENT_TAB_DARK,
        12: STEP_COMPONENT.DEFAULT_SIDE_BY_SIDE_CONTENT,
        13: STEP_COMPONENT.DEFAULT_SIDE_BY_SIDE_CONTENT,
        14: STEP_COMPONENT.START_THE_TEST,
      },
    },
    completion: {
      introStep: 1,
      outerStep: 5,
      progressStepperMax: 4,
      steps: {
        1: {
          heading: 'Verify Answer Submission',
          imgClass: 'attendance-step-3-1',
          imgDesc: 'Complete steps 1-3 before you dismiss students.',
        },
        2: { heading: 'Read Aloud to Students', imgClass: 'setup-step-4-svg', imgDesc: 'Read aloud to students' },
        3: {
          heading: 'Collect Materials for Part 1',
          imgClass: 'setup-step-4-svg',
          imgDesc: 'Collect materials for Part 1',
        },
        4: { heading: 'Transition to Exam Part 2', imgClass: 'setup-step-4-svg', imgDesc: 'Transition to Exam Part 2' },
      },
    },
    lastAttendanceStep: 14,
  },
};
